/**
 * The AI Service detail page's Agents tab (task 380; redesigned in task 796).
 *
 * The tab answers one question — *who does the work when this service is
 * called?* — and answers it in full, in place. It reads the agents referenced by
 * the service's collaboration-config definition (agent nodes carry `agentId` —
 * see `src/types/definition.ts`) and presents them master–detail: a rail of every
 * agent in flow order, and a complete profile of the selected one.
 *
 * What replaced what: the previous tab was a collapsible row per agent showing
 * the active prompt text and three links out, two of which landed on the same
 * page and one of which (History) landed on a page with no history view. Nothing
 * about the model, tools, schemas, or flow position was shown, although the
 * backend already returned all of it.
 *
 * Two properties this deliberately keeps:
 *
 *   1. **Read-first.** The tab introduces no editing surface of its own. Every
 *      mutation affordance either links into the existing Advanced pages or
 *      opens an existing modal, so no new permission rule is needed — the same
 *      position the tab has held since task 380.
 *   2. **Service-scoped.** It shows each agent *as this flow configures it*,
 *      including node-level LLM overrides and autonomy settings that the
 *      Advanced → Agents page structurally cannot show, because an agent there
 *      has no flow.
 *
 * Panels resolve independently: each fetches what it needs and renders its own
 * loading or empty state, so a slow MCP tool-count never holds up the prompt.
 */
import { useEffect, useMemo, useState } from 'react';
import { useAgent } from '@/hooks/useAgents';
import { useCollaborationConfig } from '@/hooks/useCollaborationConfigs';
import { CapabilityExplainer } from '@/components/shared/ConceptExplainers';
import { AgentRail } from './agents/AgentRail';
import { AgentIdentityHeader } from './agents/AgentIdentityHeader';
import { AgentModelPanel } from './agents/AgentModelPanel';
import { AgentPromptPanel } from './agents/AgentPromptPanel';
import { AgentCapabilitiesPanel } from './agents/AgentCapabilitiesPanel';
import { AgentIoContractPanel } from './agents/AgentIoContractPanel';
import { AgentFlowRolePanel } from './agents/AgentFlowRolePanel';
import { AgentActivityPanel } from './agents/AgentActivityPanel';
import { agentEntriesFromDefinition, type AgentFlowEntry } from './agents/agentFlow';

/**
 * Re-exported from `agents/agentFlow` — this has been the tab's published helper
 * since task 380 and is imported from here by its tests and by anything else
 * that needs the agent ids a definition references.
 */
export { agentIdsFromDefinition } from './agents/agentFlow';

interface ServiceAgentsTabProps {
  collaborationConfigId: string | null;
  /**
   * The AI Service (endpoint) this tab belongs to. Used only to scope and link
   * the per-agent activity panel (task 797): its run rows point at this
   * service's own `/services/:id/runs/:collaborationId` route.
   */
  serviceId?: string;
}

const emptyStateClasses =
  'rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-10 text-center text-sm text-gray-500 dark:text-gray-400';

interface AgentProfileProps {
  entry: AgentFlowEntry;
  definition: Record<string, unknown> | null | undefined;
  collaborationConfigId: string | null;
  serviceId?: string;
}

function AgentProfile({ entry, definition, collaborationConfigId, serviceId }: AgentProfileProps) {
  const { data: agent, isLoading, isError } = useAgent(entry.agentId);

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-sm text-gray-500 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
        Loading agent…
      </div>
    );
  }

  if (isError || !agent) {
    return (
      <div
        data-testid="agent-profile-error"
        className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
      >
        Agent <span className="font-mono text-xs">{entry.agentId}</span> could not be loaded. It may
        have been deleted while this service&rsquo;s flow still references it.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid={`agent-profile-${agent.id}`}>
      <AgentIdentityHeader agent={agent} nodes={entry.nodes} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* The model is resolved against the FIRST node appearance; a flow that
            runs one agent at several nodes lists every node's override in the
            Role-in-this-flow panel below. */}
        <AgentModelPanel agent={agent} nodeLlmConfigId={entry.nodes[0]?.llmConfigId} />
        <AgentCapabilitiesPanel agent={agent} nodes={entry.nodes} />
      </div>

      <AgentPromptPanel agentId={agent.id} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <AgentIoContractPanel agent={agent} />
        <AgentFlowRolePanel definition={definition} nodes={entry.nodes} />
      </div>

      <AgentActivityPanel
        agentId={agent.id}
        collaborationConfigId={collaborationConfigId}
        serviceId={serviceId}
      />
    </div>
  );
}

export function ServiceAgentsTab({ collaborationConfigId, serviceId }: ServiceAgentsTabProps) {
  const { data: config, isLoading } = useCollaborationConfig(collaborationConfigId ?? undefined);

  const entries = useMemo(
    () => agentEntriesFromDefinition(config?.definition),
    [config]
  );

  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);

  /**
   * Auto-select the first agent in flow order, and re-select it if the flow
   * changes under the current selection (a config edit can remove the selected
   * agent, which would otherwise leave the profile pane blank with no
   * explanation).
   */
  useEffect(() => {
    if (entries.length === 0) {
      setSelectedAgentId(null);
      return;
    }
    setSelectedAgentId((current) =>
      current && entries.some((entry) => entry.agentId === current) ? current : entries[0].agentId
    );
  }, [entries]);

  if (!collaborationConfigId) {
    return (
      <div className={emptyStateClasses}>
        This service has no collaboration config attached, so no agents to show.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400">Loading agents…</div>
      </div>
    );
  }

  if (entries.length === 0) {
    return <div className={emptyStateClasses}>This service&rsquo;s config references no agents.</div>;
  }

  const selected = entries.find((entry) => entry.agentId === selectedAgentId) ?? entries[0];

  return (
    <div className="space-y-4">
      {/* First contact with the capability concept (task 539): this tab is
          where the pieces an AI Service is made of first appear. */}
      <CapabilityExplainer />

      <div className="flex flex-col gap-5 md:flex-row md:items-start">
        {/* Below md the rail stacks above the profile. At md and up it holds its
            width and sticks, so switching agents stays reachable while a long
            profile scrolls — the intent of the mockup's fixed-width column,
            without nesting a second scrollbar inside the page. */}
        <div className="md:w-[260px] md:shrink-0 md:sticky md:top-4">
          <AgentRail
            entries={entries}
            selectedAgentId={selected.agentId}
            onSelect={setSelectedAgentId}
          />
        </div>

        <div className="min-w-0 flex-1">
          <AgentProfile
            key={selected.agentId}
            entry={selected}
            definition={config?.definition}
            collaborationConfigId={collaborationConfigId}
            serviceId={serviceId}
          />
        </div>
      </div>
    </div>
  );
}

export default ServiceAgentsTab;
